/**
 * Servicio para procesar tracking numbers en lotes usando el endpoint track-and-update de Envía.com
 */
import axiosInstance from '../../api/axiosInstance'
import type {
  TrackUpdateResponse,
  ProcessProgress,
} from '../../types/cron/enviaManual'

export class EnviaTrackUpdateService {
  private static readonly BATCH_SIZE = 10
  private static readonly MAX_TRACKING_NUMBERS = 50

  /**
   * Procesar tracking numbers en lotes de 10
   * El endpoint tiene un límite de 50, pero procesamos de 10 en 10 para mejor control
   */
  static async processTrackingNumbers(
    trackingNumbers: string[],
    onProgress?: (progress: ProcessProgress) => void
  ): Promise<TrackUpdateResponse> {
    if (trackingNumbers.length === 0) {
      throw new Error('No hay tracking numbers para procesar')
    }

    // Validar que no exceda el límite máximo
    if (trackingNumbers.length > this.MAX_TRACKING_NUMBERS) {
      throw new Error(
        `Máximo ${this.MAX_TRACKING_NUMBERS} tracking numbers permitidos. Se recibieron ${trackingNumbers.length}`
      )
    }

    // Filtrar tracking numbers vacíos o inválidos
    const validTrackingNumbers = trackingNumbers
      .map((tn) => tn.trim())
      .filter((tn) => tn.length > 0)

    if (validTrackingNumbers.length === 0) {
      throw new Error('No hay tracking numbers válidos para procesar')
    }

    // Dividir en lotes de 10
    const batches: string[][] = []
    for (let i = 0; i < validTrackingNumbers.length; i += this.BATCH_SIZE) {
      batches.push(validTrackingNumbers.slice(i, i + this.BATCH_SIZE))
    }

    const totalBatches = batches.length
    let processedGuides = 0
    const allDetails: TrackUpdateResponse['details'] = []
    let totalUpdated = 0
    let totalSkipped = 0
    let totalNotFound = 0
    let totalNotDelivered = 0
    let totalDelivered = 0

    // Procesar cada lote secuencialmente
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex]

      try {
        // Consultar endpoint
        const response = await axiosInstance.post<TrackUpdateResponse>(
          '/envia-com/track-and-update',
          {
            trackingNumbers: batch,
          }
        )

        const batchResult = response.data

        // Consolidar resultados
        allDetails.push(...batchResult.details)
        totalUpdated += batchResult.updated_count
        totalSkipped += batchResult.skipped_count
        totalNotFound += batchResult.not_found_count
        totalNotDelivered += batchResult.not_delivered_count
        totalDelivered += batchResult.delivered_count

        processedGuides += batch.length

        // Notificar progreso
        if (onProgress) {
          onProgress({
            currentBatch: batchIndex + 1,
            totalBatches,
            processedGuides,
            totalGuides: validTrackingNumbers.length,
            currentBatchResults: batchResult,
          })
        }

        // Pequeño delay entre lotes para evitar sobrecarga
        if (batchIndex < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500))
        }
      } catch (error: any) {
        console.error(`Error procesando lote ${batchIndex + 1}:`, error)

        // Agregar errores al detalle
        for (const trackingNumber of batch) {
          allDetails.push({
            tracking_number: trackingNumber,
            status: null,
            action: 'error',
            reason:
              error.response?.data?.message ||
              `Error al procesar: ${error.message}`,
          })
        }

        processedGuides += batch.length

        // Notificar progreso incluso con error
        if (onProgress) {
          onProgress({
            currentBatch: batchIndex + 1,
            totalBatches,
            processedGuides,
            totalGuides: validTrackingNumbers.length,
            currentBatchResults: {
              success: false,
              message: `Error en lote ${batchIndex + 1}`,
              timestamp: new Date().toISOString(),
              tracking_numbers_queried: batch.length,
              batches_processed: 1,
              tracking_results_count: 0,
              delivered_count: 0,
              updated_count: 0,
              skipped_count: 0,
              not_found_count: 0,
              not_delivered_count: 0,
              details: [],
            },
          })
        }
      }
    }

    // Construir respuesta consolidada
    const consolidatedResponse: TrackUpdateResponse = {
      success: true,
      message: `Procesados ${processedGuides} tracking numbers en ${totalBatches} lote(s)`,
      timestamp: new Date().toISOString(),
      tracking_numbers_queried: validTrackingNumbers.length,
      batches_processed: totalBatches,
      tracking_results_count: allDetails.length,
      delivered_count: totalDelivered,
      updated_count: totalUpdated,
      skipped_count: totalSkipped,
      not_found_count: totalNotFound,
      not_delivered_count: totalNotDelivered,
      details: allDetails,
    }

    return consolidatedResponse
  }
}

